import {
  faExclamationTriangle,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@realtyka/real-ui';
import useDeviceType from '../../../../hooks/useDeviceType';
import { cn } from '../../../../utils/classUtils';

type BannerVariant = 'pending' | 'info' | 'mismatch';

interface LicenseStatusBannerProps {
  variant: BannerVariant;
  onSendForReview?: () => void;
  isSubmitting?: boolean;
  isFormValid?: boolean;
}

const bannerConfig = {
  pending: {
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    iconColor: 'text-yellow-600',
    icon: faExclamationTriangle,
    title: 'Pending Approval',
    message:
      "The license has been sent for manual review. Please hold on while it's being reviewed.",
    showButton: false,
  },
  info: {
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-500',
    icon: faInfoCircle,
    title: 'This image will appear on your public agent profile',
    message: null,
    showButton: false,
  },
  mismatch: {
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconColor: 'text-amber-500',
    icon: faExclamationTriangle,
    title: 'License Number Not Detected',
    message:
      'Please verify your uploaded image clearly shows your complete Real Estate License information before continuing.',
    showButton: false,
  },
};

const LicenseStatusBanner: React.FC<LicenseStatusBannerProps> = ({
  variant,
  onSendForReview,
  isSubmitting = false,
  isFormValid = true,
}) => {
  const config = bannerConfig[variant];
  const { isMobile } = useDeviceType();

  return (
    <div
      className={`${config.bgColor} box-border content-stretch flex gap-4 items-start justify-center p-4 relative rounded-lg size-full border ${config.borderColor}`}
    >
      <div
        className={cn(
          'basis-0 content-stretch flex gap-4 grow items-center justify-between min-h-px min-w-px relative shrink-0',
          isMobile && 'flex-col gap-4',
        )}
      >
        <div className='basis-0 content-stretch flex flex-col gap-1 grow items-start justify-start leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-sm'>
          <div className='content-stretch flex gap-2 items-start justify-start relative shrink-0 w-full'>
            <FontAwesomeIcon
              icon={config.icon}
              className={config.iconColor}
              style={{ marginTop: '2px' }}
            />
            <div className='basis-0 flex flex-col font-semibold grow justify-center min-h-px min-w-px relative shrink-0 text-neutral-800'>
              <p className='leading-[1.25] font-inter'>{config.title}</p>
            </div>
          </div>
          {config.message && (
            <div className='flex flex-col font-normal justify-center relative shrink-0 text-reskin-grey-900 w-full'>
              <p className='leading-[1.25] font-inter'>{config.message}</p>
            </div>
          )}
        </div>
        {config.showButton && onSendForReview && (
          <Button
            onClick={onSendForReview}
            variant='filled'
            color='primary'
            fullWidth={isMobile}
            size={isMobile ? 'sm' : 'md'}
            disabled={isSubmitting || !isFormValid}
          >
            Send for review
          </Button>
        )}
      </div>
    </div>
  );
};

export default LicenseStatusBanner;
